import React, { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { AuthContext, IntlContext } from "context";
import { useProviderServiceContact } from "hooks/useProviderContact";
import { ProviderContactServiceDialog } from "components/provider/ProviderContactServiceDialog";
import { isProviderAvailable } from "helpers/isProviderAvailable";
import type { ServiceInterface } from "types/service";
import { ProviderIdentity } from "components/provider/ProviderIdentity";
import { ProviderServiceChips } from "components/provider/ProviderServiceChips";
import { buildRoute } from "routes";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card, CardDescription, CardFooter } from "components/ui/card";
import { Role } from "types/user";
import FavoriteProviderButton from "components/Favorite/FavoriteProviderButton";

export const ProviderListCard: React.FC<{ service: ServiceInterface }> = ({ service }) => {
    const intl = useContext(IntlContext);
    const { account, currentRole } = useContext(AuthContext);
    const navigate = useNavigate();
    const catalog = useMemo(() => service.provider_services ?? [], [service.provider_services]);
    const { open, setOpen, onContactClick, contactService } = useProviderServiceContact(catalog, service.id);
    const isOwnProfile = account?.resources?.provider?.[0]?.id === service.provider_id;
    const isAvailable = isProviderAvailable(service);
    const showContactButton = currentRole !== Role.PROVIDER && !isOwnProfile && isAvailable;

    return (
        <Card
            variant="catalog"
            className="border-slate-100"
            onClick={() =>
                navigate(buildRoute.provider(service.id), {
                    state: { from: window.location.pathname + window.location.search },
                })
            }
        >
            <div className="flex items-start justify-between gap-2">
                <ProviderIdentity provider={service.provider} size="sm" className="min-w-0 flex-1" />
                <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                    <FavoriteProviderButton
                        providerId={service.provider_id}
                        clientsFav={service.clients_fav}
                        size="sm"
                    />
                </div>
            </div>

            <ProviderServiceChips catalog={catalog} chipLevel="category" />

            <div className="flex-1 overflow-hidden">
                <CardDescription className="line-clamp-3 leading-relaxed">
                    {service.provider.description ??
                        service.description ??
                        intl.formatMessage("provider.card.no_description")}
                </CardDescription>
            </div>

            <CardFooter className="mt-auto items-center justify-between gap-3 p-0" onClick={(e) => e.stopPropagation()}>
                <div>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {intl.formatMessage("provider.card.price_from")}
                    </p>
                    <p className="text-lg font-extrabold leading-tight">
                        {Number(service.price_from ?? service.price).toFixed(2)}€
                        <span className="text-xs font-normal text-muted-foreground">
                            {intl.formatMessage("provider.card.price_per_hour")}
                        </span>
                    </p>
                </div>

                {showContactButton ? (
                    <>
                        <Button size="lg" onClick={onContactClick}>
                            {intl.formatMessage("provider.card.contact", { firstName: service.provider.first_name })}
                        </Button>
                        <ProviderContactServiceDialog
                            open={open}
                            onOpenChange={setOpen}
                            services={catalog}
                            firstName={service.provider.first_name}
                            onSelect={contactService}
                        />
                    </>
                ) : (
                    !isAvailable && (
                        <Badge variant="unavailable">{intl.formatMessage("provider.card.unavailable")}</Badge>
                    )
                )}
            </CardFooter>
        </Card>
    );
};
